import { FormInput, FormSelect } from "@actinc/dls/components";
import _ from "lodash/fp";
import { Controller, useFormContext } from "react-hook-form";

import { provideDefaultMessages } from "../../services/formUtils";

const ControlledFormSelect = ({
  control,
  defaultValue,
  disabled,
  fullWidth,
  id,
  inputProps,
  label,
  multiple = false,
  name,
  onChange: handleChange,
  options = [],
  disablePlaceholder,
  readonly,
  rules = {},
  trigger,
  ...props
}) => {
  const formContext = useFormContext();
  let setFormValue;
  if (formContext) {
    const { setValue } = formContext;
    setFormValue = setValue;
  }

  return (
    (!!readonly && (
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({
          field: { onBlur, onChange, ref, value },
          fieldState: { error },
        }) => {
          const val = value ?? "";
          const displayValue = multiple
            ? options
                ?.filter((o) => value.includes(o.value))
                ?.map((o) => o.label)
                .join(",")
            : options?.find(
                (o) =>
                  o.value ===
                  (typeof o.value === "string" ? val.toString() : val),
              )?.label;

          return (
            <FormInput
              error={Boolean(error)}
              errorMessage={error?.message}
              formControlProps={{
                size: "small",
                sx: {
                  marginBottom: 2,
                  minWidth: () => {
                    const longestOption = _.max(
                      options.map(({ label = "" }) => {
                        return label.toString()?.length;
                      }),
                    );
                    if (fullWidth) return `0px`;
                    if (options.length)
                      return `${
                        `${
                          rules?.required === true && longestOption < 13
                            ? 13
                            : longestOption
                        }` *
                          0.6 +
                        4
                      }rem`;
                    return `200px`;
                  },
                  "& .MuiInputBase-root": {
                    marginTop: 0,
                  },
                },
              }}
              fullWidth={fullWidth}
              id={id}
              inputProps={{
                ...inputProps,
                readOnly: readonly,
              }}
              inputRef={ref}
              label={label}
              name={name}
              onBlur={(e) => {
                onBlur(e);
                trigger && trigger();
              }}
              onChange={(e) => {
                onChange(e);
                trigger && trigger();
              }}
              required={!!rules.required}
              value={displayValue || ""}
            />
          );
        }}
        rules={provideDefaultMessages(rules)}
        {...props}
      />
    )) || (
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        render={({
          field: { onBlur, onChange, ref, value = "" },
          fieldState: { error },
        }) => {
          let fieldValue = value;
          if (_.isNull(value)) {
            fieldValue = multiple ? [] : "";
          }
          return (
            <FormSelect
              disabled={disabled}
              error={Boolean(error)}
              errorMessage={error?.message}
              formControlProps={{
                margin: "none",
                size: "small",
                sx: {
                  marginBottom: 2,
                  minWidth: () => {
                    const longestOption = _.max([
                      ...options.map(({ label = "" }) => {
                        return label.toString()?.length;
                      }),
                      label?.length,
                    ]);
                    if (fullWidth) return `0px`;
                    if (options.length)
                      return `${
                        `${
                          rules?.required === true && longestOption < 13
                            ? 13
                            : longestOption
                        }` *
                          0.6 +
                        4
                      }rem`;
                    return `200px`;
                  },
                  "& .MuiInputBase-root": {
                    marginTop: 0,
                  },
                },
              }}
              fullWidth={fullWidth}
              id={id}
              inputProps={inputProps}
              inputRef={ref}
              label={label}
              multiple={multiple}
              name={name}
              onBlur={(e) => {
                onBlur(e);
                trigger && trigger();
              }}
              onChange={(e) => {
                if (setFormValue) {
                  setFormValue(name, e.target.value, { shouldDirty: true });
                }

                handleChange && handleChange(e);
                onChange(e);
                trigger && trigger();
              }}
              options={options}
              placeholderIsDisabled={disablePlaceholder || !!rules.required}
              required={!!rules.required}
              value={fieldValue}
              variant="standard"
            />
          );
        }}
        rules={provideDefaultMessages(rules)}
        {...props}
      />
    )
  );
};

export { ControlledFormSelect };
